import React from "react";
import { createRoot } from "react-dom/client"; // Thay thế ReactDOM bằng createRoot
import TagManager from "react-gtm-module";
import { BrowserRouter as Router } from "react-router-dom";
import { registerFlowStepNextHook } from "@adaptis/flow-components";
import * as Sentry from "@sentry/react";
// @ts-ignore
import markerSDK from "@marker.io/browser";
import App from "./App";
import _ from "./imports/lodash-imports";
import "./styles/styles.scss";
import { applySessionRewindAfterStep1Submit } from "./util/session-rewind-identify";
import { attachSessionRewindToSentry } from "./util/session-rewind-sentry";

registerFlowStepNextHook("step-1", applySessionRewindAfterStep1Submit);

// Google Tag Manager
const tagManagerArgs = {
  gtmId: "GTM-WZH46SW",
};
TagManager.initialize(tagManagerArgs);

// Get domain
const domain = _.replace(
  _.replace(_.replace(window.location.origin, "http://", ""), "https://", ""),
  "/",
);

// Marker.io
if (domain === "uat-quote.petsy.com.au") {
  const widget = markerSDK.loadWidget({
    destination: "61b7da3d58936e092b3b66b7", // <- Your unique destination ID
  });
}

// Sentry.init({
//   dsn: "https://5a1e1edbfeab6df06313ab560bbe07cb@o4509059574398976.ingest.de.sentry.io/4509059576102992",
//   // Tắt hết default integrations để tránh popup quyền truy cập thiết bị (vd. do Replay hoặc integration mặc định khác).
//   defaultIntegrations: false,
//   integrations: [
//     Sentry.browserTracingIntegration(),
//     // Sentry.browserApiErrorsIntegration(),
//     // Sentry.globalHandlersIntegration(),
//     // Sentry.dedupeIntegration(),
//     // Sentry.breadcrumbsIntegration(),
//     // Sentry.linkedErrorsIntegration(),
//     // Sentry.inboundFiltersIntegration(),
//     // Sentry.functionToStringIntegration(),
//     // Sentry.httpContextIntegration(),
//     // KHÔNG thêm Sentry.replayIntegration() — gây popup "Access other apps and services on this device".
//   ],
//   // Tracing
//   tracesSampleRate: 1.0, //  Capture 100% of the transactions
//   // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
//   // tracePropagationTargets: ["localhost", /^https:\/\/quote\.staging\.petsy\.com\.au/, /^https:\/\/quote\.petsy\.com\.au/],
//   beforeSend(event) {
//     // Log error nhưng không ngăn chặn việc gửi lên Sentry
//     console.error('Sentry captured error:', event);
//     return event;
//   }
// });

/** Lỗi “giả” từ WebView iOS: console/SDK gọi postMessage khi native chưa đăng ký handler — không nên log Sentry hay console.error (dễ gọi lại chính bridge hỏng). */
function isWebKitMessageHandlerNoise(event: ErrorEvent): boolean {
  const parts = [event.message, event.error?.message, String(event.error ?? '')].filter(Boolean);
  return parts.some((t) =>
    /webkit\.messageHandlers|messageHandlers\[.*\]\.postMessage/i.test(t),
  );
}

function isWebKitMessageHandlerNoiseReason(reason: unknown): boolean {
  const text =
    reason && typeof reason === 'object' && 'message' in reason && typeof (reason as Error).message === 'string'
      ? (reason as Error).message
      : String(reason ?? '');
  return /webkit\.messageHandlers|messageHandlers\[.*\]\.postMessage/i.test(text);
}

Sentry.init({
  // dsn: "https://caf5138f7a3106133bacc095e734e344@o4509058037776384.ingest.us.sentry.io/4509546472865797", // development
  dsn: "https://be745364e8f668b097025812b3489105@o4509058037776384.ingest.us.sentry.io/4509546471292933", // production
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  ignoreErrors: [
    /webkit\.messageHandlers/i,
    /messageHandlers\[.*\]\.postMessage/i,
  ],
});

attachSessionRewindToSentry();

// Global error handlers - "nuốt" lỗi để không crash app
window.addEventListener('error', (event) => {
  if (isWebKitMessageHandlerNoise(event)) {
    event.preventDefault();
    return false;
  }
  console.error('Global error caught and handled:', event.error);
  console.error('Error details:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
  });

  // Gửi lên Sentry để tracking
  Sentry && Sentry.captureException(event.error, {
    tags: {
      errorType: 'global_error',
      handled: 'true'
    },
    extra: {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    }
  });

  // Ngăn chặn lỗi lan ra ngoài và crash app
  event.preventDefault();
  return false;
});

window.addEventListener('unhandledrejection', (event) => {
  if (isWebKitMessageHandlerNoiseReason(event.reason)) {
    event.preventDefault();
    return false;
  }
  console.error('Unhandled promise rejection caught and handled:', event.reason);
  console.error('Promise rejection details:', {
    reason: event.reason,
    promise: event.promise,
  });

  // Kiểm tra nếu là React error
  const isReactError = event.reason && event.reason.toString().includes("React");
  if (isReactError) {
    console.error("React error caught and handled:", event.reason);
  }

  // Helper function để safely convert to string
  const safeToString = (value: any): string => {
    try {
      if (typeof value === "symbol") {
        return value.toString();
      }
      if (value && typeof value.toString === "function") {
        return value.toString();
      }
      return JSON.stringify(value) || "undefined";
    } catch (e) {
      return "undefined";
    }
  };

  // Gửi lên Sentry để tracking
  Sentry && Sentry.captureException(safeToString(event.reason), {
    tags: {
      errorType: 'unhandled_rejection',
      handled: 'true',
      isReactError: isReactError ? 'true' : 'false'
    },
    extra: {
      promise: safeToString(event.promise),
      reason: safeToString(event.reason),
      isReactError
    }
  });

  // Ngăn chặn lỗi lan ra ngoài và crash app
  event.preventDefault();
  return false;
});

// Sentry.init({
//   dsn: "https://5a1e1edbfeab6df06313ab560bbe07cb@o4509059574398976.ingest.de.sentry.io/4509059576102992",
//   // Adds request headers and IP for users, for more info visit:
//   // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
//   sendDefaultPii: true,
//   integrations: [
//   ],
// });

function ErrorButton() {
  return (
    <button
      onClick={() => {
        throw new Error("This is your first error!");
      }}
    >
      Break the world
    </button>
  );
}

// Render ứng dụng
const container = document.getElementById("root");
const root = createRoot(container!, {
  onRecoverableError: (error) => Sentry.captureException(error),
});
root.render(
  // @ts-ignore - Fix tạm thời cho lỗi TypeScript
  // <React.StrictMode>
  <Router>
    {/* <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <App />
    </Sentry.ErrorBoundary> */}
    <App />
    {/* <ErrorButton /> */}
  </Router>,
  // </React.StrictMode>
);
